import { ArrowLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActionData, useMatches, useNavigation } from 'react-router';
import { useFetcher, useLocation, useNavigate } from 'react-router-dom';

import { Assets } from '~/assets.g/assets';
import { AppRouteKey, PlatformRoleEnum } from '~/core/router/route_types';
import { useBreadcrumbs, useFarmerReportBreadcrumbs } from '~/core/router/router_utils';
import { AppRoutes } from '~/core/router/routes';
import { BulkInviteComponent } from '~/features/user_management/presentation/components/bulk_invite_component';
import { InviteUserDrawer } from '~/features/user_management/presentation/components/invite_user_drawer';
import { UserManagementLoaderData } from '~/features/user_management/presentation/pages/user_management_page';

import { CustomButton } from '../components/custom_button';
import { CustomToast, ToastType } from '../components/custom_toast';
import { useLayoutStore } from '../stores/layoutStore';

interface TopBarProps {
  // Add props if needed in the future
  currentPath?: string;
  loaderData?: any;
}

type ToastState = {
  show: boolean;
  message?: string;
  type?: ToastType;
};

// export const loader = BulkInviteHandler.getBulkUploadHistory()

// Same result as path.replace(/\/+$/, ''), without regex backtracking
function stripTrailingSlashes(path: string): string {
  let end = path.length;
  while (end > 0 && path[end - 1] === '/') end--;
  return path.slice(0, end);
}

export function TopBar({ currentPath, loaderData }: TopBarProps): React.ReactElement {
  const { t } = useTranslation([
    'userManagement',
    'home',
    'rolesManagement',
    'organizationSettings',
    'audit',
    'farmerReport',
    'header',
    'seasonReport',
    'userReport',
    'productionHead',
    'agronomist',
  ]);

  const pathToMapTitle: Record<string, string> = {
    [AppRoutes.HOME.path]: t('homeTitle', { ns: 'home' }),
    [AppRoutes.PRODUCTION_HEAD_SEASON_EDIT.path]: t('editSeasonTitle', { ns: 'productionHead' }),
    [AppRoutes.USER_MANAGEMENT.path]: t('userManagementHeader', { ns: 'userManagement' }),
    [AppRoutes.ROLES_MANAGEMENT.path]: t('roleManagementHeader', { ns: 'rolesManagement' }),
    [AppRoutes.ORGANIZATION_PROFILE_SETTINGS.path]: t('pageTitle', { ns: 'organizationSettings' }),
    [AppRoutes.AUDIT_FARMER.path]: t('auditFarmer', { ns: 'audit' }),
    [AppRoutes.AUDIT_PROFILE.path]: t('auditProfile', { ns: 'audit' }),
    [AppRoutes.PRODUCTION_HEAD_SEASON.path]: t('productionSeason', { ns: 'productionHead' }),
    [AppRoutes.AGRONOMIST_SEASONS.path]: t('seasons', { ns: 'agronomist' }),
    [AppRoutes.AGRONOMIST_SEASON_PLAN.path]: t('seasonPlan', { ns: 'agronomist' }),
    [AppRoutes.SEASON_REPORT.path]: t('seasonReportTitle', { ns: 'seasonReport' }),
    [AppRoutes.USER_REPORT_REGION_MANAGER.path]: t('userReportTitle', { ns: 'userReport' }),
  };

  const matches = useMatches();
  const navigate = useNavigate();
  const location = useLocation(); // <-- here

  // Option 1: Using explicit type checking
  const data = matches.find((match) => {
    const matchData = match.data as UserManagementLoaderData;
    return matchData?.platformRoles !== undefined || matchData?.bulkInviteTemplate !== undefined;
  })?.data as UserManagementLoaderData;

  const actionData = useActionData<{ success?: boolean; jobId?: string; error?: string }>();

  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    show: false,
    message: '',
    type: ToastType.Success,
  });

  // Instead of useLoaderData, use useFetcher for data fetching
  const fetcher = useFetcher<UserManagementLoaderData>();

  useEffect(() => {
    //handleBulkInviteSuccess();
  }, [actionData?.success]);

  const toggleBulkUploadView = () => setShowBulkUpload((prev) => !prev);

  const handleInviteSuccess = async () => {
    setToast({ message: t('inviteSuccessMessage'), type: ToastType.Success, show: true });
  };

  // inside TopBar component

  const handleBulkInviteSuccess = (jobId?: string | null) => {
    setToast({ message: t('uploadSuccess'), type: ToastType.Success, show: true });
    // Optionally close the drawer if you want:
    // setShowBulkUpload(false);

    // if you want to do something with jobId, do it here
    // console.log('bulk invite jobId', jobId);
  };

  const handleBulkInviteError = (message?: string) => {
    setToast({
      message: message || t('failedToCreateBulkUploadJob'),
      type: ToastType.Error,
      show: true,
    });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const isAdminOrOwner = data?.platformRoles?.some(
    (role) =>
      role.platformRoleName === PlatformRoleEnum.ADMIN ||
      role.platformRoleName === PlatformRoleEnum.OWNER
  );

  // Normalize current path (remove trailing slash)
  const pathname = stripTrailingSlashes(location.pathname);

  // Helper: true only when you're deeper than the base
  const hasChildOf = (basePath: string) => {
    const base = stripTrailingSlashes(basePath);
    return pathname.startsWith(base + '/');
  };

  // Show back for either Agronomist Season Plan children OR Production Head Season children
  const showBack = false;
  // hasChildOf(AppRoutes.AGRONOMIST_SEASON_PLAN.path) ||
  // hasChildOf(AppRoutes.PRODUCTION_HEAD_SEASON.path);

  // const showBack = useMatches().length > 1;

  // Exact equality (no trailing slashes) for showing the "Create Season" button
  const isProductionHeadSeason =
    stripTrailingSlashes(AppRoutes.PRODUCTION_HEAD_SEASON.path) === pathname;

  // 🔗 Breadcrumb parts with icon separators (uses currentPath)
  const titleOverride = pathToMapTitle[currentPath || '/'];

  const { pathname: locationPath, search: locationSearch } = useLocation();
  const isFarmerReportPage = locationPath.startsWith('/farmer-report');
  const isSeasonReportPage = locationPath.startsWith('/season-report');
  const isSeasonReportDetailPage = locationPath.match(/^\/season-report\/\d+$/);
  const isOperationalPlanVersionPage = !!locationPath.match(
    /^\/agronomist-operational-plan\/[^/]+\/[^/]+\/[^/]+/
  );
  const isHarvestRecoveryVersionPage = !!locationPath.match(
    /^\/agronomist-harvest-recovery\/[^/]+\/[^/]+\/[^/]+/
  ); // Season plan config page: exactly /agronomist-season-plan/:seasonId/:planId (no deeper)
  const isSeasonPlanConfigPage = !!locationPath.match(/^\/agronomist-season-plan\/[^/]+\/[^/]+$/);
  const seasonPlanActiveTab = isSeasonPlanConfigPage
    ? (new URLSearchParams(locationSearch).get('tab') ?? 'season')
    : null;
  // Extend this map when new tabs are added to ConfigBar.tsx
  const seasonPlanTabLabels: Record<string, string> = {
    season: t('seasonConfig', { ns: 'agronomist' }),
    operational: t('operationalPlanConfig', { ns: 'agronomist' }),
    'harvest-recovery-plan': t('harvestRecoveryPlanConfig', { ns: 'agronomist' }),
  };

  // Get persist params for season report (geography structure IDs + club)
  const getSeasonReportPersistParams = () => {
    if (!isSeasonReportPage || !loaderData?.defaultGeostructureList) return [];
    const geoIds = loaderData.defaultGeostructureList.map((g: any) => g.id);
    return [...geoIds, 'club'];
  };

  const farmerBreadcrumbParts = useFarmerReportBreadcrumbs(
    loaderData,
    isFarmerReportPage ? ['from', 'to'] : isSeasonReportPage ? getSeasonReportPersistParams() : []
  ).filter((b) => b.label !== 'Not Found');
  const breadcrumbParts = useBreadcrumbs(loaderData)
    .filter((b) => b.name !== 'Not Found')
    .map((b) => ({
      ...b,
      // Use dynamic name directly if available, otherwise translate using nameKey
      label:
        b.isDynamicName && b.name ? b.name : b.nameKey ? t(b.nameKey, { ns: 'header' }) : b.name,
    }));

  // On the config page the last route segment ("Season Plan Version") is replaced
  // by the active tab label, so we drop it from the rendered parts.
  const displayBreadcrumbParts = isSeasonPlanConfigPage
    ? breadcrumbParts.slice(0, -1)
    : breadcrumbParts;
  const breadcrumbTotal = displayBreadcrumbParts.length + (isSeasonPlanConfigPage ? 1 : 0);

  const handleBreadcrumbClick = (isClickable: boolean, path?: string) => {
    if (isClickable && path) {
      navigate(path);
    }
  };

  const handleBreadcrumbPathClick = (isClickable: boolean, path: string) =>
    isClickable && navigate(path);

  return (
    <header className="sticky left-0 right-0 top-0 z-30 flex h-20 items-center justify-between overflow-hidden bg-white px-3 pl-16 shadow-sm md:px-4 md:pl-4">
      {toast.show && (
        <CustomToast
          message={toast?.message || ''}
          type={toast.type}
          onClose={hideToast}
          duration={3000}
        />
      )}

      {/* <div className="flex items-center gap-4"> */}
      <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap [scrollbar-width:none]">
        {/* Back (only when AGRONOMIST_SEASON_PLAN has a next route) */}
        {showBack && (
          <button
            type="button"
            data-name="back-btn"
            onClick={() => navigate(-1)}
            className="rounded-full p-2 transition hover:bg-neutralUltraLight"
            aria-label="Go back"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-700" />
          </button>
        )}

        {/* Breadcrumb title with icon separator */}
        {!isFarmerReportPage &&
          !isSeasonReportPage &&
          !isOperationalPlanVersionPage &&
          !isHarvestRecoveryVersionPage && (
            <div className="flex items-center gap-2 text-xl font-semibold">
              {
                // --- Default Breadcrumb UI ---
                breadcrumbParts.map((breadcrumb, idx) => {
                  const total = breadcrumbParts.length;
                  const isClickable = idx < total - 1;
                  const isActive = idx === total - 1;
                  const textColorClass = isActive
                    ? 'text-fontColor'
                    : isClickable
                      ? 'text-neutralDark cursor-pointer'
                      : 'text-neutralUltraDark';

                  return (
                    <React.Fragment key={`${breadcrumb.name}-${idx}`}>
                      {idx > 0 && (
                        <ChevronRight
                          className="h-6 w-6 font-semibold text-neutralMedium"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={`h6 relative font-semibold transition-all duration-300 ${textColorClass} ${
                          isClickable ? 'group' : ''
                        }`}
                        role="button"
                        tabIndex={isClickable ? 0 : -1}
                        aria-disabled={!isClickable}
                        onClick={() => handleBreadcrumbClick(isClickable, breadcrumb.path)}
                        onKeyDown={(e) => {
                          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            handleBreadcrumbClick(isClickable, breadcrumb.path);
                          }
                        }}
                      >
                        {breadcrumb.label}
                        {isClickable && (
                          <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-current transition-all duration-300 ease-in-out group-hover:w-full" />
                        )}
                      </span>
                    </React.Fragment>
                  );
                })
              }
              {isSeasonPlanConfigPage && seasonPlanActiveTab && (
                <>
                  <ChevronRight
                    className="h-6 w-6 font-semibold text-neutralMedium"
                    aria-hidden="true"
                  />
                  <span className="h6 font-semibold text-fontColor">
                    {seasonPlanTabLabels[seasonPlanActiveTab] ?? seasonPlanActiveTab}
                  </span>
                </>
              )}
            </div>
          )}

        {isOperationalPlanVersionPage && (
          <div className="flex items-center gap-2 text-xl font-semibold">
            <span className="h6 font-semibold text-fontColor">
              {t('operationalPlanVersionUpload', { ns: 'header' })}
            </span>
          </div>
        )}

        {isHarvestRecoveryVersionPage && (
          <div className="flex items-center gap-2 text-xl font-semibold">
            <span className="h6 font-semibold text-fontColor">
              {t('harvestRecoveryVersionUpload', { ns: 'header' })}
            </span>
          </div>
        )}

        {(isFarmerReportPage || isSeasonReportPage) && (
          <div className="flex flex-wrap items-center space-x-2">
            {farmerBreadcrumbParts.map(({ label, path, staticLabel }, idx) => {
              const isClickable = idx < farmerBreadcrumbParts.length - 1;
              const isActive = idx === farmerBreadcrumbParts.length - 1;
              const textColorClass = isActive
                ? 'text-fontColor'
                : isClickable
                  ? 'text-neutralDark cursor-pointer'
                  : 'text-neutralUltraDark';

              return (
                <React.Fragment key={`${label}-${idx}`}>
                  {idx > 0 && label && (
                    <ChevronRight
                      className="h-5 w-5 text-neutralMedium"
                      aria-hidden="true"
                    />
                  )}

                  {/*  Wrap each breadcrumb item */}
                  <div className="flex flex-col">
                    <span
                      className={`h6 relative text-center font-semibold transition-all duration-300 ${textColorClass} ${
                        isClickable ? 'group' : ''
                      }`}
                      role="button"
                      tabIndex={isClickable ? 0 : -1}
                      aria-disabled={!isClickable}
                      onClick={() => handleBreadcrumbPathClick(isClickable, path)}
                      onKeyDown={(e) => {
                        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleBreadcrumbPathClick(isClickable, path);
                        }
                      }}
                    >
                      {label}
                      {isClickable && (
                        <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-current transition-all duration-300 ease-in-out group-hover:w-full" />
                      )}
                    </span>

                    {/*  Static label below */}
                    {label && staticLabel && (
                      <span className="mt-0.5 text-center text-xs font-medium text-neutralMedium">
                        ({t(staticLabel || 'farmerReportTitle', { ns: 'farmerReport' })})
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {isAdminOrOwner && (
        // <div className="flex items-center gap-4">
        <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap [-ms-overflow-style:none]">
          {/* Bulk upload button */}
          <CustomButton
            dataName="bulk-upload-btn"
            // dataOrgId={user.orgId}
            onClick={() => setShowBulkUpload(true)}
            buttonType="outlined"
            variant="surface"
            width="w-auto"
            className="px-3"
            iconClassName="max-lg:mr-0"
            iconLeft={
              <img
                src={Assets.images.bulkUpload}
                alt="bulk upload"
                className="h-5 w-5"
              />
            }
          >
            <span className="hidden lg:inline">{t('bulkUploadButton')}</span>
          </CustomButton>

          {/* Invite users button */}
          <CustomButton
            dataName="invite-user-btn"
            width="max-w-[368px]"
            variant="primary"
            buttonType="filled"
            iconClassName="max-lg:mr-0"
            iconLeft={
              <img
                src={Assets.images.addUser}
                alt="add user"
              />
            }
            className="p-3"
            onClick={() => setIsInviteDrawerOpen(true)}
          >
            <span className="hidden lg:inline">{t('inviteUserButton')}</span>
          </CustomButton>

          {/* Bulk Upload Drawer */}

          <BulkInviteComponent
            isOpen={showBulkUpload}
            onClose={() => setShowBulkUpload(false)}
            onBack={() => setShowBulkUpload(false)}
            onSuccess={handleBulkInviteSuccess}
            onError={handleBulkInviteError}
            template={data?.bulkInviteTemplate}
          />

          {/* Invite User Drawer */}
          <InviteUserDrawer
            isOpen={isInviteDrawerOpen}
            onClose={() => setIsInviteDrawerOpen(false)}
            platformRoles={data?.platformRoles || []}
            onSuccess={handleInviteSuccess}
          />
        </div>
      )}

      {isProductionHeadSeason && (
        // <div className="flex items-center gap-4">
        <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:hidden">
          <CustomButton
            variant="primary"
            buttonType="filled"
            width="w-auto md:max-w-[368px]"
            iconLeft={
              <img
                src={Assets.images.addUser}
                alt="add user"
                className=""
              />
            }
            className="p-2 lg:p-3"
            onClick={() => navigate(AppRoutes?.[AppRouteKey.PRODUCTION_HEAD_SEASON_CREATE]?.path)}
          >
            <span className="hidden lg:inline">{t('createSeason', { ns: 'productionHead' })}</span>
          </CustomButton>
        </div>
      )}

      {/* Top Progress Bar stuck to bottom of topbar */}
      <TopProgressBar />
    </header>
  );
}

function TopProgressBar() {
  const navigation = useNavigation();
  const isPendingNavigation = useLayoutStore((state) => state.isPendingNavigation);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  // Active when either React Router is navigating OR SecureLink is validating
  const isActive = navigation.state !== 'idle' || isPendingNavigation;

  useEffect(() => {
    // Check for debug mode every 500ms so console changes trigger a re-render
    const debugInterval = setInterval(() => {
      const isDebug = typeof window !== 'undefined' && !!(window as any).__SHOW_PROGRESS__;
      if (isDebug !== debugMode) {
        setDebugMode(isDebug);
        if (isDebug) {
          setVisible(true);
          setProgress(75);
        } else if (!isActive) {
          setVisible(false);
          setProgress(0);
        }
      }
    }, 500);

    return () => clearInterval(debugInterval);
  }, [debugMode, isActive]);

  useEffect(() => {
    if (debugMode) return;

    let interval: NodeJS.Timeout;

    if (isActive) {
      setVisible(true);
      setProgress((prev) => (prev < 10 ? 10 : prev));

      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          const remaining = 90 - prev;
          return prev + remaining / 20;
        });
      }, 500);
    } else if (!isActive && visible) {
      setProgress(100);
      const timer = setTimeout(() => {
        setVisible(false);
        const resetTimer = setTimeout(() => setProgress(0), 300);
        return () => clearTimeout(resetTimer);
      }, 300);
      return () => clearTimeout(timer);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, visible, debugMode]);

  return (
    <div
      id="top-progress-bar"
      className="pointer-events-none absolute bottom-0 left-0 right-0 h-[3px]"
      style={{
        opacity: visible || debugMode ? 1 : 0,
        transition: 'opacity 0.3s ease-out',
      }}
    >
      <div
        className="progress-bar-fill"
        style={{
          width: `${progress}%`,
        }}
      />
    </div>
  );
}
